import { useState } from 'react';
import axios from 'axios';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { toast } from 'sonner';
import { 
  Cloud, 
  HardDrive, 
  Globe, 
  RefreshCw, 
  Upload, 
  CheckCircle, 
  XCircle, 
  AlertTriangle,
  Loader2,
  Image,
  Video,
  FileQuestion,
  ArrowRight
} from 'lucide-react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function S3MigrationPage() {
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [eventUrlStatus, setEventUrlStatus] = useState(null);
  const [landingMediaStatus, setLandingMediaStatus] = useState(null);
  const [migrationResult, setMigrationResult] = useState(null);

  // Check Event Image URLs
  const checkEventUrls = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/check-image-urls`, {
        
      });
      setEventUrlStatus(response.data);
      toast.success('Event URL audit complete');
    } catch (error) {
      console.error('Error checking event URLs:', error);
      toast.error(error.response?.data?.detail || 'Failed to check event URLs');
    } finally {
      setLoading(false);
    }
  };

  // Check Landing Media URLs
  const checkLandingMediaUrls = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/admin/check-landing-media-urls`, {
        
      });
      setLandingMediaStatus(response.data);
      toast.success('Landing media URL audit complete');
    } catch (error) {
      console.error('Error checking landing media URLs:', error);
      toast.error(error.response?.data?.detail || 'Failed to check landing media URLs');
    } finally {
      setLoading(false);
    }
  };

  // Run all audits
  const runFullAudit = async () => {
    setLoading(true);
    try {
      const [eventRes, mediaRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/check-image-urls`),
        axios.get(`${API_URL}/api/admin/check-landing-media-urls`)
      ]);
      setEventUrlStatus(eventRes.data);
      setLandingMediaStatus(mediaRes.data);
      toast.success('Full audit complete');
    } catch (error) {
      console.error('Error running audit:', error);
      toast.error(error.response?.data?.detail || 'Failed to run audit');
    } finally {
      setLoading(false);
    }
  };

  // Migrate Event Images to S3
  const migrateEventImages = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/admin/migrate-images-to-s3`, {}, {
        
      });
      setMigrationResult({ type: 'events', data: response.data });
      toast.success(`Migrated ${response.data.migrated} event images to S3`);
      // Refresh audit
      checkEventUrls();
    } catch (error) {
      console.error('Error migrating event images:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate event images');
    } finally {
      setMigrating(false);
    }
  };

  // Migrate Landing Media to S3
  const migrateLandingMedia = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/admin/migrate-landing-media-to-s3`, {}, {
        
      });
      setMigrationResult({ type: 'landing', data: response.data });
      toast.success(`Migrated ${response.data.migrated} landing media items to S3`);
      // Refresh audit
      checkLandingMediaUrls();
    } catch (error) {
      console.error('Error migrating landing media:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate landing media');
    } finally {
      setMigrating(false);
    }
  };

  // Migrate All
  const migrateAll = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const [eventRes, mediaRes] = await Promise.all([
        axios.post(`${API_URL}/api/admin/migrate-images-to-s3`, {}),
        axios.post(`${API_URL}/api/admin/migrate-landing-media-to-s3`, {})
      ]);
      setMigrationResult({
        type: 'all',
        events: eventRes.data,
        landing: mediaRes.data
      });
      toast.success('All migrations complete');
      // Refresh audits
      runFullAudit();
    } catch (error) {
      console.error('Error migrating all:', error);
      toast.error(error.response?.data?.detail || 'Failed to migrate');
    } finally {
      setMigrating(false);
    }
  };

  // Fix all URLs to direct S3 URLs
  const fixAllUrls = async () => {
    setMigrating(true);
    setMigrationResult(null);
    try {
      const response = await axios.post(`${API_URL}/api/admin/fix-all-urls-to-s3`, {}, {
        
      });
      setMigrationResult({ type: 'fix-urls', data: response.data });
      toast.success(`Fixed URLs: ${response.data.events_fixed} events, ${response.data.landing_media_fixed} media, ${response.data.profiles_fixed} profiles`);
      // Refresh audits
      runFullAudit();
    } catch (error) {
      console.error('Error fixing URLs:', error);
      toast.error(error.response?.data?.detail || 'Failed to fix URLs');
    } finally {
      setMigrating(false);
    }
  };

  const StatCard = ({ title, icon: Icon, s3Count, localCount, externalCount, unknownCount, total }) => (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className="w-5 h-5" />
          {title}
        </CardTitle>
        <CardDescription>Total: {total} items</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4 text-green-500" />
              <span className="text-sm">S3 Cloud</span>
            </div>
            <Badge variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
              {s3Count}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <HardDrive className="w-4 h-4 text-yellow-500" />
              <span className="text-sm">Local Storage</span>
            </div>
            <Badge variant="outline" className={`${localCount > 0 ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' : 'bg-muted'}`}>
              {localCount}
            </Badge>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-500" />
              <span className="text-sm">External URLs</span>
            </div>
            <Badge variant="outline" className="bg-blue-500/10 text-blue-500 border-blue-500/20">
              {externalCount}
            </Badge>
          </div>
          {unknownCount > 0 && (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileQuestion className="w-4 h-4 text-gray-500" />
                <span className="text-sm">Unknown</span>
              </div>
              <Badge variant="outline" className="bg-gray-500/10 text-gray-500 border-gray-500/20">
                {unknownCount}
              </Badge>
            </div>
          )}
        </div>
        
        {localCount > 0 && (
          <Alert className="mt-4 border-yellow-500/50 bg-yellow-500/10">
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
            <AlertDescription className="text-yellow-500">
              {localCount} items need migration to S3
            </AlertDescription>
          </Alert>
        )}
        
        {localCount === 0 && total > 0 && (
          <Alert className="mt-4 border-green-500/50 bg-green-500/10">
            <CheckCircle className="h-4 w-4 text-green-500" />
            <AlertDescription className="text-green-500">
              All items are on S3 or external CDN
            </AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );

  return (
    <Layout>
      <div className="container max-w-6xl py-8" data-testid="s3-migration-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold mb-2">S3 Cloud Migration</h1>
          <p className="text-muted-foreground">
            Migrate local files to AWS S3 cloud storage for better performance and reliability
          </p>
        </div>

        {/* Action Buttons */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg">Migration Actions</CardTitle>
            <CardDescription>
              First run an audit to check current status, then migrate as needed
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={runFullAudit} 
                disabled={loading || migrating}
                variant="outline"
                data-testid="run-audit-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Run Full Audit
              </Button>
              
              <Button 
                onClick={migrateAll} 
                disabled={loading || migrating}
                className="bg-green-600 hover:bg-green-700"
                data-testid="migrate-all-btn"
              >
                {migrating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Upload className="w-4 h-4 mr-2" />
                )}
                Migrate Local Files to S3
              </Button>

              <Button 
                onClick={fixAllUrls} 
                disabled={loading || migrating}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="fix-urls-btn"
              >
                {migrating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ArrowRight className="w-4 h-4 mr-2" />
                )}
                Fix All URLs to Direct S3
              </Button>
            </div>
            
            <p className="text-xs text-muted-foreground mt-3">
              <strong>Fix All URLs</strong> converts /api/files/... and /api/uploads/... URLs to direct S3 URLs (https://bucket.s3...). 
              Use this if images/videos are not loading.
            </p>
          </CardContent>
        </Card>

        {/* Status Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Event Images Status */}
          <div>
            {eventUrlStatus ? (
              <StatCard
                title="Event Images"
                icon={Image}
                s3Count={eventUrlStatus.s3_count}
                localCount={eventUrlStatus.local_count}
                externalCount={eventUrlStatus.external_count}
                unknownCount={eventUrlStatus.unknown_count}
                total={eventUrlStatus.total_events}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Image className="w-5 h-5" />
                    Event Images
                  </CardTitle>
                  <CardDescription>Click "Run Full Audit" to check status</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={checkEventUrls} 
                    disabled={loading}
                    variant="outline"
                    size="sm"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Check Event URLs
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {eventUrlStatus && eventUrlStatus.local_count > 0 && (
              <Button 
                onClick={migrateEventImages} 
                disabled={migrating}
                className="mt-3 w-full"
                variant="outline"
              >
                {migrating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Migrate {eventUrlStatus.local_count} Event Images to S3
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Landing Media Status */}
          <div>
            {landingMediaStatus ? (
              <StatCard
                title="Landing Page Media"
                icon={Video}
                s3Count={landingMediaStatus.s3_count}
                localCount={landingMediaStatus.local_count}
                externalCount={landingMediaStatus.external_count}
                unknownCount={landingMediaStatus.unknown_count}
                total={landingMediaStatus.total_media}
              />
            ) : (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Video className="w-5 h-5" />
                    Landing Page Media
                  </CardTitle>
                  <CardDescription>Click "Run Full Audit" to check status</CardDescription>
                </CardHeader>
                <CardContent>
                  <Button 
                    onClick={checkLandingMediaUrls} 
                    disabled={loading}
                    variant="outline"
                    size="sm"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Check Landing Media URLs
                  </Button>
                </CardContent>
              </Card>
            )}
            
            {landingMediaStatus && landingMediaStatus.local_count > 0 && (
              <Button 
                onClick={migrateLandingMedia} 
                disabled={migrating}
                className="mt-3 w-full"
                variant="outline"
              >
                {migrating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <>
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Migrate {landingMediaStatus.local_count} Landing Media to S3
                  </>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Migration Results */}
        {migrationResult && (
          <Card className="border-green-500/50">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-green-500">
                <CheckCircle className="w-5 h-5" />
                Migration Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              {migrationResult.type === 'all' ? (
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">Event Images</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl font-bold text-green-500">{migrationResult.events.migrated}</div>
                        <div className="text-muted-foreground">Migrated</div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl font-bold">{migrationResult.events.already_s3}</div>
                        <div className="text-muted-foreground">Already on S3</div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl font-bold text-yellow-500">{migrationResult.events.skipped}</div>
                        <div className="text-muted-foreground">Skipped</div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Landing Media</h4>
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl font-bold text-green-500">{migrationResult.landing.migrated}</div>
                        <div className="text-muted-foreground">Migrated</div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl font-bold">{migrationResult.landing.already_s3}</div>
                        <div className="text-muted-foreground">Already on S3</div>
                      </div>
                      <div className="p-3 rounded-lg bg-muted">
                        <div className="text-2xl font-bold text-yellow-500">{migrationResult.landing.skipped}</div>
                        <div className="text-muted-foreground">Skipped</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : migrationResult.type === 'fix-urls' ? (
                <div className="space-y-4">
                  <h4 className="font-medium mb-2">URLs Fixed to Direct S3</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="p-3 rounded-lg bg-muted">
                      <div className="text-2xl font-bold text-green-500">{migrationResult.data.events_fixed}</div>
                      <div className="text-muted-foreground">Event Images</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                      <div className="text-2xl font-bold text-green-500">{migrationResult.data.landing_media_fixed}</div>
                      <div className="text-muted-foreground">Landing Media</div>
                    </div>
                    <div className="p-3 rounded-lg bg-muted">
                      <div className="text-2xl font-bold text-green-500">{migrationResult.data.profiles_fixed}</div>
                      <div className="text-muted-foreground">Profile Pictures</div>
                    </div>
                  </div>
                  <Alert className="border-blue-500/50 bg-blue-500/10">
                    <CheckCircle className="h-4 w-4 text-blue-500" />
                    <AlertDescription className="text-blue-500">
                      All URLs now point directly to S3. Images and videos should load correctly.
                    </AlertDescription>
                  </Alert>
                </div>
              ) : (
                <div className="grid grid-cols-4 gap-4 text-sm">
                  <div className="p-3 rounded-lg bg-muted">
                    <div className="text-2xl font-bold">{migrationResult.data.total_events || migrationResult.data.total_media}</div>
                    <div className="text-muted-foreground">Total</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted">
                    <div className="text-2xl font-bold text-green-500">{migrationResult.data.migrated}</div>
                    <div className="text-muted-foreground">Migrated</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted">
                    <div className="text-2xl font-bold">{migrationResult.data.already_s3}</div>
                    <div className="text-muted-foreground">Already S3</div>
                  </div>
                  <div className="p-3 rounded-lg bg-muted">
                    <div className="text-2xl font-bold text-yellow-500">{migrationResult.data.skipped}</div>
                    <div className="text-muted-foreground">Skipped</div>
                  </div>
                </div>
              )}
              
              {migrationResult.data?.failed?.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-medium mb-2 text-red-500 flex items-center gap-2">
                    <XCircle className="w-4 h-4" />
                    Failed Items
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {migrationResult.data.failed.map((item, idx) => (
                      <div key={item.event_id || item.media_id || idx} className="text-sm p-2 bg-red-500/10 rounded border border-red-500/20">
                        <span className="font-medium">{item.title || item.event_id || item.media_id}</span>
                        <span className="text-muted-foreground ml-2">- {item.error}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Instructions */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg">How It Works</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm dark:prose-invert max-w-none">
            <ol className="list-decimal list-inside space-y-2 text-muted-foreground">
              <li><strong>Run Full Audit</strong> - Checks all image and video URLs in the database</li>
              <li><strong>Review Status</strong> - See how many files are on S3 vs local storage</li>
              <li><strong>Migrate to S3</strong> - Uploads local files to S3 and updates database URLs</li>
              <li><strong>Fix All URLs</strong> - Converts /api/files/... URLs to direct S3 URLs</li>
              <li><strong>Verify</strong> - Check your landing page and events to confirm images/videos load</li>
            </ol>
            
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <h4 className="font-medium mb-2">URL Types Explained:</h4>
              <ul className="space-y-1 text-sm">
                <li><span className="text-green-500">● S3 Cloud</span> - Files stored on AWS S3 (optimal)</li>
                <li><span className="text-yellow-500">● Local Storage</span> - Files on server disk (needs migration)</li>
                <li><span className="text-blue-500">● External URLs</span> - Files hosted elsewhere like Unsplash (no action needed)</li>
              </ul>
            </div>
            
            <div className="mt-4 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <h4 className="font-medium mb-2 text-red-500">Important: S3 Bucket Configuration</h4>
              <p className="text-sm text-muted-foreground mb-2">
                For images/videos to load, your S3 bucket must allow public read access. Add this bucket policy:
              </p>
              <pre className="text-xs bg-background p-2 rounded overflow-x-auto">
{`{
  "Version": "2012-10-17",
  "Statement": [{
    "Sid": "PublicReadGetObject",
    "Effect": "Allow",
    "Principal": "*",
    "Action": "s3:GetObject",
    "Resource": "arn:aws:s3:::YOUR_BUCKET_NAME/*"
  }]
}`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
